// src/Layout.js
import React, { useEffect, useContext, useState } from 'react';
import { BackendProvider, BackendContext } from './Context/BackendContext';
import AppNavbar from './Components/AppNavbar';
import { Outlet } from 'react-router-dom';
import { Hub } from 'aws-amplify/utils';
import LogInRewardModal from './Components/LogInRewardModal';
import ChooseUsernameModal from './Components/ChooseUsernameModal';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

async function waitForSignedIn(maxMs = 5000) {
  const start = Date.now();
  // Call once immediately (this kicks off the code->token exchange if needed)
  let session = await fetchAuthSession().catch(() => null);

  while (Date.now() - start < maxMs) {
    if (session?.tokens?.idToken) return session;
    await new Promise(r => setTimeout(r, 150));
    session = await fetchAuthSession().catch(() => null);
  }
  return null;
}

const Layout = () => {
  const { 
    fetchUserProfile, 
    clearUserState, 
    checkDailyReward, 
    claimDailyReward, 
    userProfile, 
    fetchUserArtworks,
    checkUsernameAvailability,
    setUsername,
  } = useContext(BackendContext);

  const [showRewardModal, setShowRewardModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  
  useEffect(() => {
    //TODO:This runs on every page change: we only want to fetch the user profile once when the app starts
    const callFetchUserProfile = async() => {
      await fetchUserProfile();
    }
    const clearUserProfile = async() => {
      await clearUserState();
    }
    //console.log("🟢 AppWithUserFetch: refetching user profile once");
    callFetchUserProfile();

    const unsubscribe = Hub.listen('auth', ({ payload: { event } }) => {
      if (event === 'signedIn') {
        //console.log("🟢 AppWithUserFetch: detected signIn event, refetching user profile");
        callFetchUserProfile();
      } else if (event === 'signedOut') {
        //console.log("🟢 AppWithUserFetch: detected signOut event, clearing user profile");
        clearUserProfile();
        setShowUsernameModal(false);
        setShowRewardModal(false);
      } else if (event === 'tokenRefresh') {
        //console.log("🟢 AppWithUserFetch: detected tokenRefresh event, refetching user profile")
        callFetchUserProfile();
      }
    });
    return () => unsubscribe();
  }, []);

  // When we have a profile, decide which modal(s) to show
  useEffect(() => {
    // Check if the user is eligible for a daily reward
    const afterProfile  = async() => {
      if (!userProfile) return;

      // 1) Username gate: show if missing/empty
      const hasUsername = !!userProfile.username; // adjust if your field is different
      setShowUsernameModal(!hasUsername);

      // 2) Daily reward (only if username already set)
      if (hasUsername) {
        const eligible = await checkDailyReward();
        setShowRewardModal(eligible);
        await fetchUserArtworks();
      }
    }

    // Fetch user artworks when userProfile is available
    const fetchArtworks = async () => {
      if (userProfile) {
        await fetchUserArtworks();
      }
    };

    afterProfile();
    fetchArtworks();

  }, [userProfile]);

  const onClaimDailyReward = async() => {
    await claimDailyReward();
    setShowRewardModal(false);
  } 

  const suggested = userProfile?.email?.split("@")?.[0]?.replace(/[^a-z0-9_]/gi, "_")?.slice(0, 20) || "";

  return (
    <div className="min-h-screen w-full bg-slate-900 dark text-foreground bg-background">
      <AppNavbar /> {/* Only mounted once */}

      <LogInRewardModal 
        isOpen={showRewardModal}
        day={userProfile?.logInStreak ? userProfile.logInStreak + 1 : 1}
        onClose={() => setShowRewardModal(false)}
        onClaim={() => onClaimDailyReward()}
      />

      <ChooseUsernameModal
        isOpen={showUsernameModal}
        onClose={() => setShowUsernameModal(false)}   // you may choose not to allow closing until set
        onSave={async (u) => { await setUsername(u); }} // updates Cognito + DynamoDB
        checkAvailable={checkUsernameAvailability}
        defaultSuggestion={suggested}
      />

      {/* Main content area where child routes will render */}
      <div className="">
        <Outlet /> {/* Child routes will render here */}
      </div>
    </div>
  );
};

export default Layout;
